/**
 * "How would you like to hear from us?" delivery-preference picker (postal-updates.md scope §1) —
 * a native `<fieldset>`/radiogroup of Email / Mail / Both, default Email. Reused on SubscribePage
 * and PreferencesPage. Native `<input type="radio">` inside a `<label>` (not a role="radio" custom
 * widget) so keyboard/screen-reader semantics are free — arrow keys move the native radio group,
 * Tab exits, the label association is automatic.
 *
 * The per-option descriptions + the group hint make the frequency expectation explicit (Lister,
 * 2026-08-16): postal mail is used SPARINGLY — for important notices only, never the routine email
 * cadence — so "Both" reads as "email for updates, mail for the rare important notice," not
 * "everything by mail." Each radio keeps a clean accessible NAME (aria-label = the bare option
 * label, so name-based queries/AT hear "Mail" not "Mail Important notices only"); the description
 * rides aria-describedby, and the group hint is wired to the radiogroup the same way.
 */
import type { DeliveryMethod } from "../api/types.js";

const OPTIONS: Array<{ value: DeliveryMethod; label: string; desc: string }> = [
  { value: "email", label: "Email", desc: "Regular city updates" },
  { value: "mail", label: "Mail", desc: "Important printed notices only" },
  { value: "both", label: "Both", desc: "Email updates + important notices by mail" },
];

interface Props {
  idPrefix: string;
  value: DeliveryMethod;
  onChange: (next: DeliveryMethod) => void;
}

export function deliveryMethodFieldId(idPrefix: string): string {
  return `field-${idPrefix}-legend`;
}

export function DeliveryMethodField({ idPrefix, value, onChange }: Props): JSX.Element {
  const name = `${idPrefix}-delivery-method`;
  const legendId = deliveryMethodFieldId(idPrefix);
  const hintId = `field-${idPrefix}-delivery-hint`;

  return (
    <fieldset className="td-fieldset">
      <legend className="td-fieldset__legend" id={legendId}>
        How would you like to hear from us?
      </legend>
      <p className="td-fieldset__hint" id={hintId}>
        We send occasional updates by email. Postal mail is used <strong>sparingly</strong> — for
        important notices only (like a boil-water advisory or a public hearing), not the regular
        email updates.
      </p>
      <div
        className="td-delivery-cards"
        role="radiogroup"
        aria-labelledby={legendId}
        aria-describedby={hintId}
      >
        {OPTIONS.map((opt) => {
          const inputId = `field-${idPrefix}-delivery-${opt.value}`;
          const descId = `${inputId}-desc`;
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={inputId}
              className={`td-delivery-card${selected ? " is-selected" : ""}`}
            >
              <input
                id={inputId}
                type="radio"
                name={name}
                value={opt.value}
                checked={selected}
                onChange={() => onChange(opt.value)}
                aria-label={opt.label}
                aria-describedby={descId}
              />
              <span className="td-channel-card__label">{opt.label}</span>
              <span className="td-delivery-card__desc" id={descId}>
                {opt.desc}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
